import type { NotCompletedNotificationValues } from './useNotCompletedNotificationValues'

import { useState, useCallback, useEffect, useRef } from 'react'

import { updateNotCompletedNotificationConfig } from '@/features/domain/territory'
import { useAppDispatch } from '@/store'

import { useTexts } from '../useTexts'
import { convertValues } from '../convertValues'

/**
 * That hook returns a toggle method for NotCompletedNotification that will allow the read-only form to
 * enable/disable the notification and an action's description that will be used in confirmation
 * modal
 */
export function useNotCompletedNotificationToggle(formValues: NotCompletedNotificationValues) {
  const dispatch = useAppDispatch()
  const values = useRef(formValues)
  const texts = useTexts()

  const [toggleDescription, setToggleDescription] = useState(() =>
    values.current.enabled ? texts.disableDescription : texts.enableDescription,
  )

  useEffect(() => {
    values.current = formValues

    setToggleDescription(
      values.current.enabled ? texts.disableDescription : texts.enableDescription,
    )
  }, [texts, formValues])

  // Toggle method
  const onToggle = useCallback(() => {
    const currentValues = values.current
    const goingToEnable = !currentValues.enabled

    const payload = {
      ...convertValues(currentValues),
      enabled: goingToEnable,
    }

    dispatch(updateNotCompletedNotificationConfig(payload))
  }, [dispatch])

  return { onToggle, toggleDescription }
}
